/**
 * Layered hand-drawn portrait from `/public/mugshot/` PNGs. Back-hair pairs
 * with chosen front-hair (not separately cycleable). Use `mugshotAssetUrl`.
 */

export const MUGSHOT_CATEGORIES = [
  'menton',
  'nez',
  'bouche',
  'moustache',
  'yeux',
  'sourcils',
  'cheveux',
  'accessoire',
] as const;

export type MugshotCategory = (typeof MUGSHOT_CATEGORIES)[number];

/** Bottom→top z-order. `cheveuxBack` derived from `cheveux` via `HAIR_BACK_FILE`. */
export type RenderSlot = MugshotCategory | 'cheveuxBack';

export const RENDER_ORDER: readonly RenderSlot[] = [
  'cheveuxBack',
  'menton',
  'nez',
  'bouche',
  'moustache',
  'yeux',
  'sourcils',
  'cheveux',
  'accessoire',
];

/** `file: null` = empty slot (e.g. no glasses). Cycle wraps through all. */
type Variant = { id: string; file: string | null };

export const MUGSHOT_PARTS: Record<MugshotCategory, readonly Variant[]> = {
  menton: [
    { id: 'propre', file: 'Menton-propre.png' },
    { id: 'gordon', file: 'Menton-gordon.png' },
    { id: 'goudron', file: 'Menton-goudron.png' },
  ],
  nez: [
    { id: 'none', file: null },
    { id: 'propre', file: 'Nez-propre.png' },
    { id: 'gordon', file: 'Nez-gordon.png' },
    { id: 'goudron', file: 'Nez-goudron.png' },
  ],
  bouche: [
    { id: 'none', file: null },
    { id: 'propre', file: 'Bouche-propre.png' },
    { id: 'gordon', file: 'Bouche-gordon.png' },
    { id: 'goudron', file: 'Bouche-goudron.png' },
  ],
  moustache: [
    { id: 'none', file: null },
    { id: 'droit', file: 'Moustache-droit.png' },
    { id: 'pattes', file: 'Moustache-pattes.png' },
  ],
  yeux: [
    { id: 'none', file: null },
    { id: 'propre', file: 'Yeux-propre.png' },
    { id: 'gordon', file: 'Yeux-gordon.png' },
    { id: 'goudron', file: 'Yeux-goudron.png' },
  ],
  sourcils: [
    { id: 'none', file: null },
    { id: 'propre', file: 'Sourcils-propre.png' },
    { id: 'gordon', file: 'Sourcils-gordon.png' },
    { id: 'goudron', file: 'Sourcils-goudron.png' },
  ],
  cheveux: [
    { id: 'chauve', file: 'Cheveux-Avant-chauve.png' },
    { id: 'court', file: 'Cheveux-Avant-court.png' },
    { id: 'vrac', file: 'Cheveux-Avant-vrac.png' },
    { id: 'longs', file: 'Cheveux-Avant-longs.png' },
    { id: 'couettes', file: 'Cheveux-Avant-couettes.png' },
    { id: 'italien', file: 'Cheveux-Avant-italien.png' },
  ],
  accessoire: [
    { id: 'none', file: null },
    { id: 'lunettes', file: 'Accessoire-lunettes.png' },
    { id: 'grain-beaute', file: 'Accessoire-grain-beaute.png' },
  ],
};

/** Paired back-hair per front-hair. `null` = no back layer. Must cover every variant id. */
const HAIR_BACK_FILE: Record<string, string | null> = {
  chauve: null,
  court: 'Cheveux-Arriere-court.png',
  vrac: null,
  longs: 'Cheveux-Arriere-longs.png',
  couettes: 'Cheveux-Arriere-longs.png',
  italien: null,
};

export type MugshotConfig = Readonly<Record<MugshotCategory, string>>;

const MUGSHOT_BASE = '/mugshot/';

export const mugshotAssetUrl = (file: string): string =>
  `${MUGSHOT_BASE}${encodeURIComponent(file)}`;

export const variantFile = (category: MugshotCategory, id: string): string | null => {
  const v = MUGSHOT_PARTS[category].find((x) => x.id === id);
  return v?.file ?? MUGSHOT_PARTS[category][0]?.file ?? null;
};

/** Resolve any render slot to its file, given the current config. */
export const renderSlotFile = (slot: RenderSlot, config: MugshotConfig): string | null => {
  if (slot === 'cheveuxBack') return HAIR_BACK_FILE[config.cheveux] ?? null;
  return variantFile(slot, config[slot]);
};

const pick = <T>(arr: readonly T[], rand: () => number): T => arr[Math.floor(rand() * arr.length)]!;

/** Excluded from random gen but cycleable. Moustache + accessoire stay optional. */
const REQUIRED_CATEGORIES: ReadonlySet<MugshotCategory> = new Set([
  'menton',
  'nez',
  'bouche',
  'yeux',
  'sourcils',
  'cheveux',
]);

/** Bias rare bits to stay rare. Unlisted categories sample uniform. */
const RANDOM_WEIGHTS: Partial<Record<MugshotCategory, Record<string, number>>> = {
  moustache: { none: 7, droit: 1, pattes: 1 },
  accessoire: { none: 6, lunettes: 2, 'grain-beaute': 1 },
};

const weightedPick = (
  variants: readonly { id: string }[],
  weights: Record<string, number>,
  rand: () => number,
): string => {
  let total = 0;
  for (const v of variants) total += weights[v.id] ?? 0;
  if (total <= 0) return pick(variants, rand).id;
  let roll = rand() * total;
  for (const v of variants) {
    const w = weights[v.id] ?? 0;
    if (w <= 0) continue;
    if (roll < w) return v.id;
    roll -= w;
  }
  // biome-ignore lint/style/noNonNullAssertion: caller guarantees non-empty variants
  return variants[variants.length - 1]!.id;
};

/** Required categories skip `none`; weighted categories use `RANDOM_WEIGHTS`. */
export const randomMugshot = (rand: () => number = Math.random): MugshotConfig => {
  const out: Partial<Record<MugshotCategory, string>> = {};
  for (const cat of MUGSHOT_CATEGORIES) {
    const variants = MUGSHOT_PARTS[cat];
    const pool = REQUIRED_CATEGORIES.has(cat) ? variants.filter((v) => v.id !== 'none') : variants;
    const weights = RANDOM_WEIGHTS[cat];
    out[cat] = weights ? weightedPick(pool, weights, rand) : pick(pool, rand).id;
  }
  return out as MugshotConfig;
};

/** Cycle to the next (or previous) variant in a category. */
export const cycleVariant = (
  config: MugshotConfig,
  category: MugshotCategory,
  direction: 1 | -1,
): MugshotConfig => {
  const variants = MUGSHOT_PARTS[category];
  const current = variants.findIndex((v) => v.id === config[category]);
  const idx = current < 0 ? 0 : current;
  const next = (idx + direction + variants.length) % variants.length;
  return { ...config, [category]: variants[next]?.id };
};

const isMugshotConfig = (v: unknown): v is MugshotConfig => {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return MUGSHOT_CATEGORIES.every((c) => typeof r[c] === 'string');
};

export const sanitizeMugshot = (v: unknown): MugshotConfig | null =>
  isMugshotConfig(v) ? v : null;

/** djb2 — small, fast, stable hash for seeding fallback mugshots. */
const hashId = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return h >>> 0;
};

/** Stored config else deterministic-by-id fallback (same face across clients). */
export const mugshotForPlayer = (player: { id: string; mugshot?: unknown }): MugshotConfig => {
  const stored = sanitizeMugshot(player.mugshot);
  if (stored) return stored;
  const rng = seededRngLocal(hashId(player.id));
  return randomMugshot(() => rng());
};

// Inline mulberry32 — avoids cross-package import for this one helper.
const seededRngLocal = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
